import React, { useState, useEffect } from 'react';
import Icon from '../shared/Icon';
import { useFlag } from '@unleash/proxy-client-react';
import { apiService } from '../../../services/api';
import SharePointConnectionForm from './connection-forms/SharePointConnectionForm';
import HostedPdfConnectionForm from './connection-forms/HostedPdfConnectionForm';
import './AddConnectionModal.css';

// Same modal serves create AND edit. In edit mode `connection_type` is
// locked — switching it implies a different connector + re-keyed secrets,
// which is a delete-and-recreate flow.
const AddConnectionModal = ({ isOpen, onClose, onSave, onShowNotification, editingConnection }) => {
  const sharePointEnabled = useFlag('sharepoint-connector');
  const hostedPdfEnabled = useFlag('tributary_connector_hosted_pdf');
  const isEditMode = Boolean(editingConnection && editingConnection.id);
  const [connectionType, setConnectionType] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [storageAccount, setStorageAccount] = useState('');
  const [containerName, setContainerName] = useState('');
  const [bucketName, setBucketName] = useState('');
  const [rootPrefix, setRootPrefix] = useState('');
  const [sasToken, setSasToken] = useState('');
  const [saving, setSaving] = useState(false);
  // Child forms own their state; we just hold the latest snapshot for submit.
  const [sharePointPayload, setSharePointPayload] = useState(null);
  const [hostedPdfPayload, setHostedPdfPayload] = useState(null);

  // Hydrate from editingConnection. azure_blob.location is
  // "storage_account/container_name" — split it. S3 uses account_name/location
  // for the bucket. SharePoint / hosted_pdf hydrate via the child forms' own
  // initialValues prop below.
  useEffect(() => {
    if (!isEditMode) return;
    const c = editingConnection;
    setConnectionType(c.connection_type || '');
    setDisplayName(c.display_name || '');
    setRootPrefix(c.root_prefix && c.root_prefix !== '/' ? c.root_prefix : '');
    if (c.connection_type === 'azure_blob') {
      const [acct, container] = (c.location || '').split('/');
      setStorageAccount(acct || c.account_name || '');
      setContainerName(container || '');
    } else if (c.connection_type === 'aws_s3') {
      setBucketName(c.account_name || c.location || '');
    }
  }, [isEditMode, editingConnection]);

  if (!isOpen) return null;

  const notifyError = (message) => {
    if (onShowNotification) {
      onShowNotification(message, 'error');
    } else {
      // Fallback for legacy callers that don't pass the notification handler.
      // Existing azure_blob / aws_s3 paths still rely on alert; keep behavior.
      alert(message);  // eslint-disable-line no-alert
    }
  };

  // Build the PATCH body for edit mode. Returns null and surfaces a
  // user-visible validation error when any required field is missing.
  const buildEditPatch = () => {
    const patch = { display_name: displayName };
    if (connectionType !== 'sharepoint' && connectionType !== 'hosted_pdf') {
      patch.root_prefix = rootPrefix || '/';
    }
    if (connectionType === 'azure_blob') {
      if (!storageAccount || !containerName) {
        notifyError('Storage Account and Container Name are required');
        return null;
      }
      patch.location = `${storageAccount}/${containerName}`;
      patch.account_name = storageAccount;
    } else if (connectionType === 'aws_s3') {
      if (!bucketName) {
        notifyError('Bucket Name is required');
        return null;
      }
      patch.location = `s3://${bucketName}`;
      patch.account_name = bucketName;
    } else if (connectionType === 'sharepoint') {
      if (!sharePointPayload) {
        notifyError('SharePoint form not loaded');
        return null;
      }
      const { tenant_id, client_id, client_secret, site_url, folder_path, include_subfolders, file_extensions } = sharePointPayload;
      if (!site_url) {
        notifyError('Site URL is required');
        return null;
      }
      patch.location = site_url;
      // Backend merges this into existing config — resolved site_id/drive_id
      // are preserved across partial updates.
      patch.config = {
        tenant_id: tenant_id || '',
        client_id: client_id || '',
        folder_path: folder_path || '/',
        include_subfolders: !!include_subfolders,
        file_extensions: file_extensions || [],
      };
      // Only send client_secret when the user explicitly typed one; empty
      // means "keep existing vault value."
      if (client_secret) {
        patch.client_secret = client_secret;
      }
    } else if (connectionType === 'hosted_pdf') {
      if (!hostedPdfPayload) {
        notifyError('Hosted PDF form not loaded');
        return null;
      }
      patch.config = { ...hostedPdfPayload };
    }
    return patch;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!connectionType) {
      notifyError('Please select a connection type');
      return;
    }

    if (!displayName) {
      notifyError('Please enter a display name');
      return;
    }

    // Edit-mode PATCH. Per-variant fields are reconstructed from the form
    // state; secrets are kept out (except client_secret, which the user
    // explicitly opts into rotating by typing into the empty field).
    if (isEditMode) {
      const patch = buildEditPatch();
      if (!patch) {
        // buildEditPatch surfaced its own validation error.
        return;
      }
      setSaving(true);
      try {
        await apiService.updateConnection(editingConnection.id, patch);
        if (onShowNotification) {
          onShowNotification('Connection updated', 'success');
        }
        if (onSave) onSave({ id: editingConnection.id, ...patch });
        handleClose();
      } catch (err) {
        notifyError(err.message || 'Failed to update connection');
      } finally {
        setSaving(false);
      }
      return;
    }

    const connectionData = {
      connection_type: connectionType,
      display_name: displayName,
      root_prefix: rootPrefix || '/',
    };

    if (connectionType === 'azure_blob') {
      if (!storageAccount || !containerName || !sasToken) {
        notifyError('Please fill in all Azure Blob fields (Storage Account, Container Name, and SAS Token)');
        return;
      }
      connectionData.storage_account = storageAccount;
      connectionData.container_name = containerName;
      connectionData.sas_token = sasToken;
      connectionData.location = `${storageAccount}/${containerName}`;
    } else if (connectionType === 'aws_s3') {
      if (!bucketName) {
        notifyError('Please fill in the Bucket Name');
        return;
      }
      connectionData.bucket_name = bucketName;
      connectionData.location = `s3://${bucketName}`;
    } else if (connectionType === 'sharepoint') {
      // Defense in depth — handler should also be flag-gated per CLAUDE.md rule.
      if (!sharePointEnabled) return;
      if (!sharePointPayload) {
        notifyError('Please fill in the SharePoint connection details');
        return;
      }
      const { tenant_id, client_id, client_secret, site_url } = sharePointPayload;
      if (!tenant_id || !client_id || !client_secret || !site_url) {
        notifyError('Tenant ID, Client ID, Client Secret, and Site URL are all required for SharePoint');
        return;
      }
      Object.assign(connectionData, sharePointPayload);
      // SharePoint doesn't use the legacy `location`/sparse columns; backend stores config
      // in JSONB. We still send `location` because the response model surfaces it as a
      // human-readable handle.
      connectionData.location = site_url;
      // `root_prefix` doesn't apply to SharePoint (the folder is captured in folder_path).
      delete connectionData.root_prefix;
    } else if (connectionType === 'hosted_pdf') {
      if (!hostedPdfEnabled) return;
      if (!hostedPdfPayload || !hostedPdfPayload.mode) {
        notifyError('Please fill in the Hosted PDF connection details');
        return;
      }
      if (hostedPdfPayload.mode === 'stable_urls' && (!hostedPdfPayload.urls || hostedPdfPayload.urls.length === 0)) {
        notifyError('At least one URL is required when mode is Stable URLs');
        return;
      }
      if (hostedPdfPayload.mode === 'index_url') {
        if (!hostedPdfPayload.index_url) {
          notifyError('Index URL is required when mode is Index URL');
          return;
        }
        if (!hostedPdfPayload.index_format) {
          notifyError('Index Format is required when mode is Index URL');
          return;
        }
        if (hostedPdfPayload.index_format === 'json' && !hostedPdfPayload.index_jsonpath) {
          notifyError('JSONPath is required for JSON index format');
          return;
        }
      }
      Object.assign(connectionData, hostedPdfPayload);
      // Backend persists everything in config JSONB; location is rendered server-side.
      // root_prefix isn't meaningful for Hosted PDF (no folder hierarchy).
      delete connectionData.root_prefix;
    }

    onSave(connectionData);
  };

  const handleClose = () => {
    setConnectionType('');
    setDisplayName('');
    setStorageAccount('');
    setContainerName('');
    setBucketName('');
    setRootPrefix('');
    setSasToken('');
    setSharePointPayload(null);
    setHostedPdfPayload(null);
    onClose();
  };

  return (
    <>
      <div className="connection-modal-backdrop" onClick={handleClose} />
      <div className="connection-modal">
        <div className="connection-modal-header">
          <h2 className="connection-modal-title">
            {isEditMode ? 'Edit Source Connection' : 'Add Source Connection'}
          </h2>
          <button className="connection-modal-close" onClick={handleClose}>
            <Icon name="close" />
          </button>
        </div>

        <form className="connection-modal-form" onSubmit={handleSubmit}>
          <div className="connection-modal-field">
            <label htmlFor="connection-type">Connection Type</label>
            <select
              className="resui-select"
              id="connection-type"
              value={connectionType}
              onChange={(e) => setConnectionType(e.target.value)}
              required
              disabled={isEditMode}
            >
              <option value="">Select type</option>
              <option value="azure_blob">Azure Blob</option>
              <option value="aws_s3">AWS S3</option>
              {sharePointEnabled && <option value="sharepoint">SharePoint</option>}
              {hostedPdfEnabled && <option value="hosted_pdf">Hosted PDF</option>}
            </select>
            {isEditMode && (
              <p className="connection-modal-helper-text">
                Connection type can't be changed. Delete and recreate to switch.
              </p>
            )}
          </div>

          <div className="connection-modal-field">
            <label htmlFor="display-name">Display Name</label>
            <input className="resui-input"
              id="display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Azure Blob - Leases Container"
              required
            />
          </div>

          {connectionType === 'azure_blob' && (
            <>
              <div className="connection-modal-field">
                <label htmlFor="storage-account">Storage Account</label>
                <input className="resui-input"
                  id="storage-account"
                  type="text"
                  value={storageAccount}
                  onChange={(e) => setStorageAccount(e.target.value)}
                  placeholder="account.blob.core.windows.net"
                  required
                />
              </div>

              <div className="connection-modal-field">
                <label htmlFor="container-name">Container Name</label>
                <input className="resui-input"
                  id="container-name"
                  type="text"
                  value={containerName}
                  onChange={(e) => setContainerName(e.target.value)}
                  placeholder="container"
                  required
                />
              </div>

              {!isEditMode && (
                <div className="connection-modal-field">
                  <label htmlFor="sas-token">SAS Token</label>
                  <input className="resui-input"
                    id="sas-token"
                    type="text"
                    value={sasToken}
                    onChange={(e) => setSasToken(e.target.value)}
                    placeholder="Enter SAS token"
                    required
                  />
                  <p className="connection-modal-helper-text">
                    Rotating the SAS token requires creating a new connection. Edit mode keeps the existing token in Key Vault.
                  </p>
                </div>
              )}
            </>
          )}

          {connectionType === 'aws_s3' && (
            <div className="connection-modal-field">
              <label htmlFor="bucket-name">Bucket Name</label>
              <input className="resui-input"
                id="bucket-name"
                type="text"
                value={bucketName}
                onChange={(e) => setBucketName(e.target.value)}
                placeholder="my-bucket"
                required
              />
            </div>
          )}

          {connectionType === 'sharepoint' && sharePointEnabled && (
            <SharePointConnectionForm
              onChange={setSharePointPayload}
              // site_url isn't in config — it's the `location` column.
              initialValues={
                isEditMode && editingConnection
                  ? { ...(editingConnection.config || {}), site_url: editingConnection.location || '' }
                  : undefined
              }
              lockSecret={isEditMode}
            />
          )}

          {connectionType === 'hosted_pdf' && hostedPdfEnabled && (
            <HostedPdfConnectionForm
              onChange={setHostedPdfPayload}
              initialValues={isEditMode && editingConnection ? (editingConnection.config || {}) : undefined}
            />
          )}

          {connectionType !== 'sharepoint' && connectionType !== 'hosted_pdf' && (
            <div className="connection-modal-field">
              <label htmlFor="root-prefix">Root Prefix (Optional)</label>
              <input className="resui-input"
                id="root-prefix"
                type="text"
                value={rootPrefix}
                onChange={(e) => setRootPrefix(e.target.value)}
                placeholder="e.g., leases/ or data/"
              />
              <p className="connection-modal-helper-text">
                Subfolder path inside the container/bucket.
              </p>
            </div>
          )}

          <div className="connection-modal-auth-info">
            <p>
              Authentication: In a full deployment, you would configure authentication (connection strings, IAM roles, etc.) here. This demo shows the connection structure only.
            </p>
          </div>

          <div className="connection-modal-actions">
            <button
              type="button"
              className="connection-modal-cancel"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="connection-modal-save" disabled={saving}>
              {saving ? 'Saving…' : isEditMode ? 'Save changes' : 'Save Connection'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddConnectionModal;
